import { Extension, type Range } from "@tiptap/core";
import type { Editor } from "@tiptap/react";
import { PluginKey } from "@tiptap/pm/state";
import Suggestion from "@tiptap/suggestion";
import {
  createSuggestionRenderer,
  SuggestionList,
  type SuggestionItem,
} from "./suggestion-list";

export const SLASH_PLUGIN_KEY = new PluginKey("slashCommand");

export interface SlashItem extends SuggestionItem {
  run: (editor: Editor, range: Range) => void;
}

export interface SlashCommandOptions {
  getItems: (query: string, editor: Editor) => SlashItem[];
}

export function filterSlashItems(
  items: SlashItem[],
  query: string,
): SlashItem[] {
  const q = query.trim().toLowerCase();
  if (!q) return items;
  return items.filter((item) => {
    if (item.title.toLowerCase().includes(q)) return true;
    const keywords = (item.data?.keywords as string[] | undefined) ?? [];
    return keywords.some((k) => k.toLowerCase().includes(q));
  });
}

export const SlashCommand = Extension.create<SlashCommandOptions>({
  name: "slashCommand",

  addOptions() {
    return {
      getItems: () => [],
    };
  },

  addProseMirrorPlugins() {
    return [
      Suggestion<SlashItem>({
        editor: this.editor,
        char: "/",
        pluginKey: SLASH_PLUGIN_KEY,
        allowSpaces: false,
        startOfLine: false,
        allow: ({ state, range }) => {
          const $from = state.doc.resolve(range.from);
          if ($from.parent.type.spec.code) return false;
          const textBefore = $from.parent.textBetween(
            0,
            $from.parentOffset,
            undefined,
            " ",
          );
          // textBefore excludes the "/"; allow only when preceding text is empty or ends in whitespace ("a/" must not trigger).
          return textBefore.length === 0 || /\s$/.test(textBefore);
        },
        items: ({ query }) => this.options.getItems(query, this.editor),
        command: ({ editor, range, props }) => {
          props.run(editor, range);
        },
        render: createSuggestionRenderer({
          component: SuggestionList,
          maxHeight: 420,
          mapProps: (props) => ({
            items: props.items,
            command: (item: SuggestionItem) => props.command(item),
            emptyMessage: "No matching blocks",
            columns: 2,
            width: 460,
          }),
        }),
      }),
    ];
  },
});
